import { useState, useEffect, useCallback }    from "react";
import seniorsIcon     from "../assets/seniors.svg";
import adlIcon         from "../assets/ADL.svg";
import fallsIcon       from "../assets/falls.svg";
import homeIcon        from "../assets/home.svg";
import sleepDiaryIcon  from "../assets/sleep_diary.svg";
import gaitIcon        from "../assets/gait.svg";
import caregiversIcon  from "../assets/caregivers.svg";
import notificationIcon from "../assets/notification.svg";
import aitLogo         from "../assets/ait.svg";
import useWindowSize   from "../hooks/useWindowSize";
import ProfileDropdown from "./ProfileDropdown";
import apiService from "../services/api";
import "./Sidebar.css";

/* ── Theme definitions per page ── */
const THEMES = {
  dark: {
    navBg:        "linear-gradient(135deg, #0c1e3a 0%, #162d50 50%, #1a3a66 100%)",
    navFallback:  "#0c1e3a",
    border:       "rgba(255,255,255,0.08)",
    activeBg:     "rgba(255,255,255,0.15)",
    text:         "#ffffff",
    iconFilter:   "brightness(0) invert(1)",
    btnBg:        "rgba(255,255,255,0.12)",
    iconStroke:   "white",
    iconFill:     "white",
    hamburgerBg:  "#0c1e3a",
    hamburgerBorder: "rgba(255,255,255,0.15)",
  },
  light: {
    navBg:        "linear-gradient(135deg, #1a6ad4 0%, #2b7de0 50%, #4da4f0 100%)",
    navFallback:  "#2b7de0",
    border:       "rgba(255,255,255,0.15)",
    activeBg:     "rgba(255,255,255,0.22)",
    text:         "#ffffff",
    iconFilter:   "brightness(0) invert(1)",
    btnBg:        "rgba(255,255,255,0.15)",
    iconStroke:   "white",
    iconFill:     "white",
    hamburgerBg:  "#2b7de0",
    hamburgerBorder: "rgba(255,255,255,0.2)",
  },
};

/* Map each page to a theme — all pages use dark nav */
const PAGE_THEME = {};

function getTheme(activePage) {
  return THEMES[PAGE_THEME[activePage] || "dark"];
}

const NAV_ITEMS = [
  { label: "Home",        icon: homeIcon        },
  { label: "Falls",       icon: fallsIcon       },
  { label: "ADLs",        icon: adlIcon         },
  { label: "Sleep Diary", icon: sleepDiaryIcon  },
  { label: "Gait",        icon: gaitIcon        },
  { label: "Seniors",     icon: seniorsIcon     },
  { label: "Caregivers",  icon: caregiversIcon  },
];

/* ──── Seniors Filter Button Component ──── */
function SeniorsFilterButton({ label, value, options, onChange }) {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div style={{ position: "relative" }}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        style={{
          background: value === "All" ? "rgba(255,255,255,0.08)" : "rgba(74,144,226,0.25)",
          border: `1.5px solid ${value === "All" ? "rgba(255,255,255,0.25)" : "rgba(74,144,226,0.5)"}`,
          borderRadius: 8,
          padding: "8px 14px",
          fontSize: 13,
          fontWeight: 600,
          color: "#ffffff",
          cursor: "pointer",
          display: "flex",
          alignItems: "center",
          gap: 6,
          whiteSpace: "nowrap",
        }}
      >
        <span style={{ opacity: 0.7 }}>{label}:</span>
        <span>{value}</span>
        <svg 
          width="12" 
          height="12" 
          viewBox="0 0 24 24" 
          fill="none" 
          stroke="#ffffff" 
          strokeWidth="2.5"
          style={{ 
            transform: isOpen ? "rotate(180deg)" : "rotate(0deg)",
            transition: "transform 0.2s"
          }}
        >
          <path d="M6 9l6 6 6-6"/>
        </svg>
      </button>

      {isOpen && (
        <>
          <div
            onClick={() => setIsOpen(false)}
            style={{
              position: "fixed",
              inset: 0,
              zIndex: 1000,
              background: "transparent",
            }}
          />
          <div style={{
            position: "absolute",
            top: "calc(100% + 4px)",
            left: 0,
            zIndex: 1001,
            background: "#0e2240",
            border: "1.5px solid rgba(255,255,255,0.3)",
            borderRadius: 8,
            padding: "6px",
            minWidth: 120,
            boxShadow: "0 4px 12px rgba(0,0,0,0.3)",
          }}>
            {options.map((option) => (
              <button
                key={option}
                onClick={() => {
                  onChange(option);
                  setIsOpen(false);
                }}
                style={{
                  width: "100%",
                  background: value === option ? "rgba(74,144,226,0.3)" : "transparent",
                  border: "none",
                  borderRadius: 6,
                  padding: "8px 12px",
                  fontSize: 13,
                  fontWeight: value === option ? 700 : 400,
                  color: "#ffffff",
                  cursor: "pointer",
                  textAlign: "left",
                  transition: "background 0.15s",
                }}
                onMouseEnter={(e) => {
                  if (value !== option) e.currentTarget.style.background = "rgba(255,255,255,0.08)";
                }}
                onMouseLeave={(e) => {
                  if (value !== option) e.currentTarget.style.background = "transparent";
                }}
              >
                {option}
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

export default function Sidebar({ activePage, onNavigate, seniorsFilters, caregiversFilters, fallsFilters, isNavigationLocked, onSignOut, user, selectedResidentId, onResidentSelect, isViewAllMode, onFacilityOverview }) {
    const { isMobile } = useWindowSize();
  const [mobileOpen, setMobileOpen] = useState(false);
  const t = getTheme(activePage);
  
        // Resident selector state
  const [residents, setResidents] = useState([]);
  const [residentSearchQuery, setResidentSearchQuery] = useState("");
  const [showResidentDropdown, setShowResidentDropdown] = useState(false);
  const [loadingResidents, setLoadingResidents] = useState(false);
  
    // Wrap loadResidents in useCallback to prevent unnecessary re-renders
    const loadResidents = useCallback(async () => {
        try {
          setLoadingResidents(true);
          const data = await apiService.getAllResidents();
          setResidents(data.residents || []);
        } catch (error) {
          setResidents([]);
        } finally {
          setLoadingResidents(false);
        }
      }, []); // Empty dependency array - function only created once
  
    // Load residents when component mounts
    useEffect(() => {
      loadResidents();
    }, [loadResidents]); // Now loadResidents is stable
  
                // Filter residents based on search query
    // Always show all residents regardless of page
    const residentsToShow = residents;

    const filteredResidents = residentsToShow.filter(resident => {
      if (!residentSearchQuery.trim()) return true;
      const query = residentSearchQuery.toLowerCase();
      const name = resident.status_name_sort?.split('--').slice(1, 3).join(' ').toLowerCase() || '';
      const residentId = resident.resident_id?.toLowerCase() || '';
      return name.includes(query) || residentId.includes(query);
    });
  
    // Get selected resident name
  const getSelectedResidentName = () => {
    // If in View All mode on Falls page, show "All Residents"
    if (isViewAllMode && activePage === 'Falls') {
      return 'All Residents';
    }
    
    if (!selectedResidentId) return 'Select Resident';
    const resident = residents.find(r => r.resident_id === selectedResidentId);
    if (!resident) return 'Select Resident';
        const parts = resident.status_name_sort?.split('--');
    if (parts && parts.length >= 3) {
      return `${parts[2]} ${parts[1]}`;
    }
    return 'Select Resident';
  };
  
  // Check if current page should show resident selector
  const shouldShowResidentSelector = ['Falls', 'Sleep Diary', 'Gait', 'ADLs'].includes(activePage);

  /* ── Mobile: hamburger + drawer ── */
  if (isMobile) {
    return (
      <>
        {/* Hamburger button */}
        <button
          onClick={() => setMobileOpen(o => !o)}
          style={{
            position: "fixed", top: 10, left: 10, zIndex: 1100,
            width: 38, height: 38, borderRadius: 10,
            background: t.hamburgerBg, border: `1px solid ${t.hamburgerBorder}`,
            display: "flex", alignItems: "center", justifyContent: "center",
            cursor: "pointer",
          }}
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none"
               stroke={t.iconStroke} strokeWidth="2" strokeLinecap="round">
            {mobileOpen
              ? <><path d="M18 6L6 18"/><path d="M6 6l12 12"/></>
              : <><path d="M3 6h18"/><path d="M3 12h18"/><path d="M3 18h18"/></>
            }
          </svg>
        </button>

        {/* Backdrop */}
        {mobileOpen && (
          <div
            onClick={() => setMobileOpen(false)}
            style={{ position: "fixed", inset: 0, zIndex: 1050, background: "rgba(0,0,0,0.5)" }}
          />
        )}

                {/* Drawer — slides from top */}
        <div style={{
          position: "fixed", top: 0, left: 0, right: 0, zIndex: 1060,
          background: t.navBg,
          borderBottom: `1px solid ${t.border}`,
          padding: "12px 14px",
          display: "flex", flexDirection: "column", gap: 10,
          transform: mobileOpen ? "translateY(0)" : "translateY(-100%)",
          transition: "transform 0.25s ease",
        }}>
                    {/* Nav icons row */}
                    <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap", paddingTop: 40 }}>
                      {NAV_ITEMS.map(({ label, icon }) => {
                        const active = label === activePage;
                        return (
                          <div
                            key={label}
                            onClick={() => { onNavigate(label); setMobileOpen(false); }}
                            className={`mobile-nav-icon-wrapper ${active ? 'active' : ''}`}
                            style={{
                              background: active ? t.activeBg : "transparent",
                              color: t.text,
                            }}
                          >
                            <img src={icon} alt={label} className="mobile-nav-icon" style={{ filter: t.iconFilter }} />
                            <span className="mobile-nav-label" style={{ color: t.text }}>{label}</span>
                          </div>
                        );
                      })}
                    </div>
          
                    {/* Resident Selector for mobile - Show on Falls, Sleep Diary, Gait, ADLs pages */}
                    {shouldShowResidentSelector && (
                      <div style={{
                        borderTop: `1px solid ${t.border}`,
                        paddingTop: 10,
                        marginTop: 4,
                      }}>
                        <div style={{
                          padding: "8px 10px",
                          borderRadius: 8,
                          background: "rgba(255,255,255,0.05)",
                        }}>
                          <div style={{
                            display: "flex",
                            alignItems: "center",
                            gap: 8,
                            marginBottom: 8,
                          }}>
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round">
                              <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
                              <circle cx="12" cy="7" r="4"/>
                            </svg>
                            <span style={{ fontSize: 12, fontWeight: 600, color: t.text, opacity: 0.8 }}>Select Resident</span>
                          </div>
                
                          <div style={{ position: "relative" }}>
                                                        <button
                              onClick={() => setShowResidentDropdown(!showResidentDropdown)}
                              style={{
                                width: "100%",
                                background: (selectedResidentId || isViewAllMode) ? "rgba(74,144,226,0.25)" : "rgba(255,255,255,0.08)",
                                border: `1.5px solid ${(selectedResidentId || isViewAllMode) ? "rgba(74,144,226,0.5)" : "rgba(255,255,255,0.25)"}`,
                                borderRadius: 8,
                                padding: "10px 12px",
                                fontSize: 13,
                                fontWeight: 600,
                                color: "#ffffff",
                                cursor: "pointer",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "space-between",
                                gap: 8,
                              }}
                            >
                              <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                {getSelectedResidentName()}
                              </span>
                              <svg 
                                width="12" 
                                height="12" 
                                viewBox="0 0 24 24" 
                                fill="none" 
                                stroke="#ffffff" 
                                strokeWidth="2.5"
                                style={{ 
                                  transform: showResidentDropdown ? "rotate(180deg)" : "rotate(0deg)",
                                  transition: "transform 0.2s",
                                  flexShrink: 0
                                }}
                              >
                                <path d="M6 9l6 6 6-6"/>
                              </svg>
                            </button>
                  
                            {showResidentDropdown && (
                              <>
                                <div
                                  onClick={() => setShowResidentDropdown(false)}
                                  style={{
                                    position: "fixed",
                                    inset: 0,
                                    zIndex: 1070,
                                    background: "transparent",
                                  }}
                                />
                                <div style={{
                                  position: "absolute",
                                  top: "calc(100% + 4px)",
                                  left: 0,
                                  right: 0,
                                  zIndex: 1071,
                                  background: "#0e2240",
                                  border: "1.5px solid rgba(255,255,255,0.3)",
                                  borderRadius: 8,
                                  padding: "6px",
                                  maxHeight: 250,
                                  overflowY: "auto",
                                  boxShadow: "0 4px 12px rgba(0,0,0,0.3)",
                                }}>
                                  {/* Search input */}
                                  <div style={{ padding: "6px", marginBottom: 4 }}>
                                    <input
                                      type="text"
                                      placeholder="Search by name or ID..."
                                      value={residentSearchQuery}
                                      onChange={(e) => setResidentSearchQuery(e.target.value)}
                                      onClick={(e) => e.stopPropagation()}
                                      style={{
                                        width: "100%",
                                        background: "rgba(255,255,255,0.08)",
                                        border: "1px solid rgba(255,255,255,0.2)",
                                        borderRadius: 6,
                                        padding: "8px 10px",
                                        fontSize: 12,
                                        color: "#ffffff",
                                        outline: "none",
                                      }}
                                    />
                                  </div>
                        
                                  {loadingResidents ? (
                                    <div style={{ padding: "12px", textAlign: "center", fontSize: 12, opacity: 0.7, color: "#ffffff" }}>
                                      Loading residents...
                                    </div>
                                  ) : filteredResidents.length === 0 ? (
                                    <div style={{ padding: "12px", textAlign: "center", fontSize: 12, opacity: 0.7, color: "#ffffff" }}>
                                      No residents found
                                    </div>
                                  ) : (
                                    filteredResidents.map((resident) => {
                                                                            const parts = resident.status_name_sort?.split('--');
                                      const name = parts && parts.length >= 3 ? `${parts[2]} ${parts[1]}` : 'Unknown';
                                      const isSelected = resident.resident_id === selectedResidentId;
                            
                                      return (
                                        <button
                                          key={resident.resident_id}
                                          onClick={() => {
                                            onResidentSelect?.(resident.resident_id);
                                            setShowResidentDropdown(false);
                                            setResidentSearchQuery("");
                                            setMobileOpen(false);
                                          }}
                                          style={{
                                            width: "100%",
                                            background: isSelected ? "rgba(74,144,226,0.3)" : "transparent",
                                            border: "none",
                                            borderRadius: 6,
                                            padding: "10px 12px",
                                            fontSize: 13,
                                            fontWeight: isSelected ? 700 : 400,
                                            color: "#ffffff",
                                            cursor: "pointer",
                                            textAlign: "left",
                                            transition: "background 0.15s",
                                            display: "flex",
                                            flexDirection: "column",
                                            gap: 2,
                                          }}
                                        >
                                          <div>{name}</div>
                                          <div style={{ fontSize: 10, opacity: 0.6 }}>{resident.resident_id}</div>
                                        </button>
                                      );
                                    })
                                  )}
                                </div>
                              </>
                            )}
                          </div>
                        </div>
                      </div>
                    )}
          
          {/* User info and logout */}
          <div style={{
            borderTop: `1px solid ${t.border}`,
            paddingTop: 10,
            marginTop: 4,
          }}>
                        <div style={{
              display: "flex",
              alignItems: "center",
              gap: 10,
              padding: "8px 10px",
              borderRadius: 8,
              background: "rgba(255,255,255,0.05)",
            }}>
              <div style={{
                width: 32,
                height: 32,
                borderRadius: "50%",
                background: "linear-gradient(135deg, #0a1a30 0%, #1a2d4a 100%)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: 11,
                fontWeight: 700,
                color: "#ffffff",
                border: "2px solid rgba(255,255,255,0.3)",
                flexShrink: 0,
              }}>
                {(() => {
                  const name = user?.name || user?.email?.split("@")[0] || "U";
                  const parts = name.trim().split(" ");
                  if (parts.length >= 2) {
                    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
                  }
                  return name.substring(0, 2).toUpperCase();
                })()}
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{
                  fontSize: 13,
                  fontWeight: 600,
                  color: t.text,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}>
                  {user?.name || user?.email?.split("@")[0] || "User"}
                </div>
                <div style={{
                  fontSize: 11,
                  color: t.text,
                  opacity: 0.6,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}>
                  {user?.email || "No email"}
                </div>
              </div>
            </div>
            
            <button
              onClick={() => {
                setMobileOpen(false);
                onSignOut();
              }}
              style={{
                width: "100%",
                marginTop: 8,
                background: "rgba(255,107,107,0.15)",
                border: "1px solid rgba(255,107,107,0.3)",
                borderRadius: 8,
                padding: "10px 12px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 8,
                cursor: "pointer",
                color: "#ff6b6b",
              }}
            >
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
                <polyline points="16 17 21 12 16 7" />
                <line x1="21" y1="12" x2="9" y2="12" />
              </svg>
              <span style={{ fontSize: 13, fontWeight: 600 }}>Log Out</span>
            </button>
          </div>
        </div>
      </>
    );
  }

          /* ── Desktop / Tablet: 3 content-sized pills with space between ── */
  const pillH = 52;
  const pillStyle = {
    background: "linear-gradient(180deg, #0a1a30 0%, #0e2240 40%, #112a50 100%)",
    border: "2px solid rgba(255,255,255,0.6)",
    borderRadius: 14,
    display: "flex", alignItems: "center", justifyContent: "center",
    position: "relative", overflow: "visible",
    height: pillH, flexShrink: 0,
  };

                                                                                return (
    <header style={{
      position: "fixed", top: 0, left: 0, right: 0, zIndex: 1000,
      height: 68, flexShrink: 0,
      background: "transparent",
      display: "flex", alignItems: "flex-end",
      padding: "0 60px 6px 60px",
      gap: 10,
      pointerEvents: "none", // Allow clicks to pass through empty areas
    }}>
                        {/* ── Branding: AIT Logo & FallVision ── */}
            <div style={{ 
              display: "flex", 
              alignItems: "center", 
              gap: 1, 
              padding: "0 4px 0 0",
              height: pillH,
              pointerEvents: "auto"
            }}>
              <img src={aitLogo} alt="AIT Logo" style={{ width: 60, height: 60 }} />
              <span style={{ 
                fontSize: 24, 
                fontWeight: 800, 
                color:"white", 
                letterSpacing: "1px",
                fontFamily: "'Segoe UI', 'Roboto', 'Arial', sans-serif",
                textShadow: "0 2px 4px rgba(0,0,0,0.3)"
              }}>FallVision</span>
            </div>

      {/* ── Pill 1: Nav icons ── */}
      <nav style={{ ...pillStyle, gap: 6, padding: "0 10px", pointerEvents: "auto" }}>
          {NAV_ITEMS.map(({ label, icon }) => {
          const active = label === activePage;
          const isLocked = isNavigationLocked && label !== "Seniors";
          
          return (
            <div
              key={label}
              onClick={() => {
                if (!isLocked) {
                  onNavigate(label);
                }
              }}
              title={label}
              className={`nav-icon-wrapper ${active ? 'active' : ''} ${isLocked ? 'locked' : ''}`}
            >
              <img src={icon} alt={label} className="nav-icon" style={{ filter: t.iconFilter }} />
            </div>
          );
        })}
      </nav>

      {/* Space between Pill 1 and Pills 2+3 */}
      <div style={{ flex: 1 }} />

      {/* Back arrow button - shown on Gait page when resident is selected, navigates to facility gait overview */}
      {onFacilityOverview && (
        <button
          onClick={onFacilityOverview}
          title="Back to Facility Gait Overview"
          style={{
            ...pillStyle,
            width: pillH,
            padding: 0,
            cursor: "pointer",
            pointerEvents: "auto",
            transition: "all 0.2s",
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = "linear-gradient(180deg, #0e2240 0%, #153060 40%, #1a3a6a 100%)";
            e.currentTarget.style.borderColor = "rgba(255,255,255,0.85)";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = "linear-gradient(180deg, #0a1a30 0%, #0e2240 40%, #112a50 100%)";
            e.currentTarget.style.borderColor = "rgba(255,255,255,0.6)";
          }}
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#ffffff" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <path d="M19 12H5M12 19l-7-7 7-7"/>
          </svg>
        </button>
      )}

      {/* ── Pill 2: Resident Selector OR Seniors Filters OR Caregivers Filters OR Insights and Alerts ── */}
      {shouldShowResidentSelector ? (
        <div style={{ ...pillStyle, gap: 10, padding: "0 16px", pointerEvents: "auto", position: "relative" }}>
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round">
            <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
            <circle cx="12" cy="7" r="4"/>
          </svg>

          <div style={{ position: "relative", minWidth: 200 }}>
                        <button
              onClick={() => setShowResidentDropdown(!showResidentDropdown)}
              style={{
                background: (selectedResidentId || isViewAllMode) ? "rgba(74,144,226,0.25)" : "rgba(255,255,255,0.08)",
                border: `1.5px solid ${(selectedResidentId || isViewAllMode) ? "rgba(74,144,226,0.5)" : "rgba(255,255,255,0.25)"}`,
                borderRadius: 8,
                padding: "8px 14px",
                fontSize: 13,
                fontWeight: 600,
                color: "#ffffff",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 8,
                width: "100%",
              }}
            >
              <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {getSelectedResidentName()}
              </span>
              <svg 
                width="12" 
                height="12" 
                viewBox="0 0 24 24" 
                fill="none" 
                stroke="#ffffff" 
                strokeWidth="2.5"
                style={{ 
                  transform: showResidentDropdown ? "rotate(180deg)" : "rotate(0deg)",
                  transition: "transform 0.2s",
                  flexShrink: 0
                }}
              >
                <path d="M6 9l6 6 6-6"/>
              </svg>
            </button>
            
            {showResidentDropdown && (
              <>
                <div
                  onClick={() => setShowResidentDropdown(false)}
                  style={{
                    position: "fixed",
                    inset: 0,
                    zIndex: 1000,
                    background: "transparent",
                  }}
                />
                <div style={{
                  position: "absolute",
                  top: "calc(100% + 4px)",
                  left: 0,
                  right: 0,
                  zIndex: 1001,
                  background: "#0e2240",
                  border: "1.5px solid rgba(255,255,255,0.3)",
                  borderRadius: 8,
                  padding: "6px",
                  maxHeight: 300,
                  overflowY: "auto",
                  boxShadow: "0 4px 12px rgba(0,0,0,0.3)",
                }}>
                  {/* Search input */}
                  <div style={{ padding: "6px", marginBottom: 4 }}>
                    <input
                      type="text"
                      placeholder="Search by name or ID..."
                      value={residentSearchQuery}
                      onChange={(e) => setResidentSearchQuery(e.target.value)}
                      onClick={(e) => e.stopPropagation()}
                      style={{
                        width: "100%",
                        background: "rgba(255,255,255,0.08)",
                        border: "1px solid rgba(255,255,255,0.2)",
                        borderRadius: 6,
                        padding: "6px 10px",
                        fontSize: 12,
                        color: "#ffffff",
                        outline: "none",
                      }}
                    />
                  </div>
                  
                  {loadingResidents ? (
                    <div style={{ padding: "12px", textAlign: "center", fontSize: 12, opacity: 0.7 }}>
                      Loading residents...
                    </div>
                  ) : filteredResidents.length === 0 ? (
                    <div style={{ padding: "12px", textAlign: "center", fontSize: 12, opacity: 0.7 }}>
                      No residents found
                    </div>
                  ) : (
                    filteredResidents.map((resident) => {
                      const parts = resident.status_name_sort?.split('--');
                      const name = parts && parts.length >= 3 ? `${parts[2]} ${parts[1]}` : 'Unknown';
                      const isSelected = resident.resident_id === selectedResidentId;
                      
                      return (
                        <button
                          key={resident.resident_id}
                          onClick={() => {
                            onResidentSelect?.(resident.resident_id);
                            setShowResidentDropdown(false);
                            setResidentSearchQuery("");
                          }}
                          style={{
                            width: "100%",
                            background: isSelected ? "rgba(74,144,226,0.3)" : "transparent",
                            border: "none",
                            borderRadius: 6,
                            padding: "8px 12px",
                            fontSize: 13,
                            fontWeight: isSelected ? 700 : 400,
                            color: "#ffffff",
                            cursor: "pointer",
                            textAlign: "left",
                            transition: "background 0.15s",
                            display: "flex",
                            flexDirection: "column",
                            gap: 2,
                          }}
                          onMouseEnter={(e) => {
                            if (!isSelected) e.currentTarget.style.background = "rgba(255,255,255,0.08)";
                          }}
                          onMouseLeave={(e) => {
                            if (!isSelected) e.currentTarget.style.background = "transparent";
                          }}
                        >
                          <div>{name}</div>
                          <div style={{ fontSize: 10, opacity: 0.6 }}>{resident.resident_id}</div>
                        </button>
                      );
                    })
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      ) : activePage === "Seniors" && seniorsFilters ? (
        <div style={{ ...pillStyle, gap: 10, padding: "0 16px", pointerEvents: "auto" }}>
          {/* Age Group Filter */}
          <SeniorsFilterButton
            label="Age group"
            value={seniorsFilters.selectedAgeGroup}
            options={["All", "60-70", "71-80", "81+"]}
            onChange={seniorsFilters.setSelectedAgeGroup}
          />

          {/* Sleep Quality Filter */}
          <SeniorsFilterButton
            label="Sleep quality"
            value={seniorsFilters.selectedSleepQuality}
            options={["All", "GOOD", "AVERAGE", "POOR"]}
            onChange={seniorsFilters.setSelectedSleepQuality}
          />

          {/* Risk Level Filter */}
          <SeniorsFilterButton
            label="Risk level"
            value={seniorsFilters.selectedRiskLevel}
            options={["All", "LOW", "MODERATE", "HIGH"]}
            onChange={seniorsFilters.setSelectedRiskLevel}
          />
        </div>
      ) : activePage === "Caregivers" && caregiversFilters ? (
        <div style={{ ...pillStyle, gap: 10, padding: "0 16px", pointerEvents: "auto" }}>
                    {/* Role Filter */}
          <SeniorsFilterButton
            label="Role"
            value={caregiversFilters.selectedRole}
            options={["All", "RN", "LPN", "CNA", "PT", "NP", "ADMIN"]}
            onChange={(value) => {
              if (typeof caregiversFilters.setSelectedRole === 'function') {
                caregiversFilters.setSelectedRole(value);
              }
            }}
          />

          {/* Shift Filter */}
          <SeniorsFilterButton
            label="Shift"
            value={caregiversFilters.selectedShift}
            options={["All", "DAY", "EVENING", "NIGHT"]}
            onChange={(value) => {
              if (typeof caregiversFilters.setSelectedShift === 'function') {
                caregiversFilters.setSelectedShift(value);
              }
            }}
          />

          {/* Status Filter */}
          <SeniorsFilterButton
            label="Status"
            value={caregiversFilters.selectedStatus}
            options={["All", "ACTIVE", "INACTIVE", "ON_LEAVE"]}
            onChange={(value) => {
              if (typeof caregiversFilters.setSelectedStatus === 'function') {
                caregiversFilters.setSelectedStatus(value);
              }
            }}
          />
        </div>
                                                ) : activePage === "ADLs" ? (
        // Date display - ONLY for ADLs page
        <div style={{ ...pillStyle, gap: 8, padding: "0 20px", pointerEvents: "auto" }}>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="1.8" strokeLinecap="round">
            <rect x="3" y="4" width="18" height="18" rx="2" ry="2"/>
            <line x1="16" y1="2" x2="16" y2="6"/>
            <line x1="8" y1="2" x2="8" y2="6"/>
            <line x1="3" y1="10" x2="21" y2="10"/>
          </svg>
          <span style={{ fontSize: 13, color: "#ffffff", fontWeight: 600, whiteSpace: "nowrap" }}>
            {new Date().toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
          </span>
        </div>
      ) : activePage === "Falls" && fallsFilters ? (
        // Falls page filters - Age, Risk Level, Sleep Quality
        <div style={{ ...pillStyle, gap: 10, padding: "0 16px", pointerEvents: "auto" }}>
          <SeniorsFilterButton
            label="Age"
            value={fallsFilters.selectedAge}
            options={["All", "60-70", "71-80", "81+"]}
            onChange={fallsFilters.setSelectedAge}
          />
          <SeniorsFilterButton
            label="Risk Level"
            value={fallsFilters.selectedRiskLevel}
            options={["All", "LOW", "MODERATE", "HIGH"]}
            onChange={fallsFilters.setSelectedRiskLevel}
          />
          <SeniorsFilterButton
            label="Sleep Quality"
            value={fallsFilters.selectedSleepQuality}
            options={["All", "GOOD", "AVERAGE", "POOR"]}
            onChange={fallsFilters.setSelectedSleepQuality}
          />
        </div>
      ) : (
        // Insights and Alerts - for all other pages
        <div style={{ ...pillStyle, gap: 10, padding: "0 20px", cursor: "pointer", pointerEvents: "auto" }}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="1.6" strokeLinecap="round">
            <circle cx="12" cy="12" r="10"/>
            <path d="M12 8v4l2 2"/>
          </svg>
                    <span style={{ fontSize: 14, color: "#ffffff", fontWeight: 600, whiteSpace: "nowrap" }}>Insights and Alerts</span>
        </div>
      )}

                        {/* ── Pill 3: Bell + User ── */}
      <div style={{ ...pillStyle, gap: 6, padding: "0 14px", pointerEvents: "auto" }}>
        <div style={{
          width: 38, height: 38, borderRadius: 8,
          display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer",
        }}>
          <img src={notificationIcon} alt="Notifications" style={{ width: 22, height: 22 }} />
        </div>
                        <ProfileDropdown user={user} onSignOut={onSignOut} />
      </div>
    </header>
  );
}
